import React from 'react';
import { cn } from '../../utils/cn';

interface ModalProps extends React.HTMLAttributes<HTMLDivElement> {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
  children: React.ReactNode;
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Modal({
  isOpen,
  onClose,
  title,
  size = 'md',
  showCloseButton = true,
  className,
  children,
  ...props
}: ModalProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="fixed inset-0 bg-surface/80 backdrop-blur-sm transition-opacity" />
      <div
        className={cn(
          'relative z-50 w-full rounded-xl bg-surface shadow-2xl transition-all',
          sizeClasses[size],
          className
        )}
        {...props}
      >
        {title && (
          <div className="flex items-center justify-between border-b border-outline px-6 py-4">
            <h2 className="text-xl font-semibold text-on-surface">{title}</h2>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="rounded-full p-1 text-on-surface-variant hover:bg-on-surface/10 hover:text-on-surface transition-colors"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export function useModal() {
  const [isOpen, setIsOpen] = React.useState(false);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen((prev) => !prev);

  return { isOpen, open, close, toggle };
}
